import { EditorProvider } from '@/components/ui/kibo-ui/editor';
import { cn } from '@/lib/utils';
import type { Editor, EditorEvents } from '@tiptap/core';
import { useReactFlow, useStore } from '@xyflow/react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { TextNodeProps } from '.';
import { NodeLayout } from '../layout';
import { NodeSurface } from '@/components/nodes/shared';
import { useI18n } from '@/providers/i18n';
import { useProject } from '@/providers/project';
import { useUser } from '@/hooks/use-user';
import { TextEditDialogEditor } from './text-edit-dialog-editor';
import { textToTiptapJSON } from '@/lib/utils';
import type { JSONContent } from '@tiptap/react';
import { nanoid } from 'nanoid';
import { TextCollapsedSummary } from '@/components/nodes/text/collapsed-summary';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';
import { TextTopToolbar } from './text-top-toolbar';
import { useTextSplit } from './use-text-split';
import { selectNodeSelectedById } from '../store-selectors';
import { useIsMultiSelect } from '@/components/canvas/utils/multi-select-store';

type TextPrimitiveProps = TextNodeProps & {
  title: string;
};

export const TextPrimitive = ({
  data,
  id,
  type,
  title,
}: TextPrimitiveProps) => {
  const { t } = useI18n();
  const { updateNodeData, getNode, addNodes, addEdges } = useReactFlow() as any;
  const { handleSplit: performSplit } = useTextSplit(id);
  const isPreview = usePreviewCanvas();
  const project = useProject();
  const { user } = useUser();
  const editor = useRef<Editor | null>(null);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  // 显式编辑模式：避免“选中即进入输入”导致 Cmd/Ctrl+C/V 变成复制/粘贴文字而非节点
  const [isEditing, setIsEditing] = useState(false);
  
  // 编辑对话框状态
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editContent, setEditContent] = useState<JSONContent | null>(null);
  const isUpdatingFromDialogRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 锁定状态：用于覆盖遮挡内容，防止看到提示词
  const isLocked = (() => {
    try {
      const n = getNode(id) as any;
      return Boolean(n?.data?.locked ?? (data as any)?.locked);
    } catch {
      return Boolean((data as any)?.locked);
    }
  })();

  const isCollapsed = (() => {
    try {
      const n = getNode(id) as any;
      return Boolean(n?.data?.collapsed ?? (data as any)?.collapsed);
    } catch {
      return Boolean((data as any)?.collapsed);
    }
  })();

  const isOriginalOwner = (() => {
    try {
      const originOwnerId = (project as any)?.originOwnerUserId ?? (project as any)?.userId;
      return Boolean(user?.id) && String(user?.id) === String(originOwnerId ?? '');
    } catch {
      return false;
    }
  })();

  const handleUpdate = ({ editor }: { editor: Editor }) => {
    if (isPreview) return;
    // 如果是从对话框更新，跳过这个更新以避免循环
    if (isUpdatingFromDialogRef.current) {
      isUpdatingFromDialogRef.current = false;
      return;
    }
    const json = editor.getJSON();
    const text = editor.getText();
    updateNodeData(id, { content: json, text });
  };

  const handleHardBreakKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      const ed = editor.current;
      try {
        ed?.chain().focus().setHardBreak().run();
      } catch {}
    }
  };

  const handleCreate = (props: EditorEvents['create']) => {
    editor.current = props.editor;
    // 默认不自动进入编辑态（避免抢走画布快捷键）
    try { props.editor?.setEditable?.(false); } catch {}
  };

  const openEditDialog = useCallback(() => {
    if (isLocked && !isOriginalOwner) return;
    const currentText = data.text || '';
    const currentContent = data.content || textToTiptapJSON(currentText);
    setEditContent(currentContent as JSONContent);
    setIsEditDialogOpen(true);
  }, [data.text, data.content, isLocked, isOriginalOwner]);

  // 固定白色背景
  const bgStyle = { backgroundColor: 'var(--node-bg)' } as const;

  const selectIsSelected = useMemo(() => selectNodeSelectedById(id), [id]);
  const isSelected = useStore(selectIsSelected);
  const isMultiSelect = useIsMultiSelect();
  const isSingleSelected = isSelected && !isMultiSelect;
  const canEditInline = !isPreview && isSingleSelected && !(isLocked && !isOriginalOwner);

  // Sticky-mount the heavy TipTap editor: only mount once the node has been
  // selected at least once. Avoids paying TipTap's initialization cost for
  // every prompt node on the canvas at startup, especially when many text
  // nodes exist (perf: significant first-render speedup with many nodes).
  const [editorMounted, setEditorMounted] = useState(false);
  useEffect(() => {
    if (isSelected || isEditorFocused) {
      setEditorMounted(true);
    }
  }, [isSelected, isEditorFocused]);

  useEffect(() => {
    // 当不满足可编辑条件（未单选/被锁定等）时，强制退出编辑，把快捷键还给画布
    if (!canEditInline) {
      setIsEditing(false);
      setIsEditorFocused(false);
      try {
        editor.current?.setEditable?.(false);
        editor.current?.commands.blur();
      } catch {}
      return;
    }

    // 进入/退出编辑模式：由显式 isEditing 控制
    try {
      editor.current?.setEditable?.(Boolean(isEditing));
      if (!isEditing) {
        editor.current?.commands.blur();
      }
    } catch {}
  }, [canEditInline, isEditing]);

  const enterEditMode = useCallback(() => {
    if (isPreview) return;
    if (!canEditInline) return;
    setIsEditing(true);
    // 等待 editable/pointer-events 生效后再 focus
    setTimeout(() => {
      try {
        editor.current?.setEditable?.(true);
        editor.current?.chain().focus().run();
        setIsEditorFocused(true);
      } catch {}
    }, 0);
  }, [canEditInline, isPreview]);

  const exitEditMode = useCallback(() => {
    setIsEditing(false);
    setIsEditorFocused(false);
    try {
      editor.current?.setEditable?.(false);
      editor.current?.commands.blur();
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    } catch {}
  }, []);

  const handlePaste = useCallback((clip: string) => {
    if (isPreview) return;
    const currentText = (data.text || '').trim();
    const nextText = currentText ? `${currentText}${currentText.endsWith('\n') ? '' : '\n'}${clip}` : clip;
    updateNodeData(id, { 
      content: textToTiptapJSON(nextText), 
      text: nextText 
    });
  }, [data.text, id, isPreview, updateNodeData]);

  const handleSplit = useCallback((mode: 'paragraph' | 'line' | 'sentence' | 'comma' = 'paragraph') => {
    performSplit(data.text ?? '', mode);
  }, [data.text, performSplit]);

  // 当 data.content 更新时，同步更新编辑器内容（用于编辑对话框保存后的更新）
  useEffect(() => {
    if (!editor.current) return;
    // 如果编辑器正在被用户编辑（有焦点），不更新
    if (isEditorFocused) return;
    
    const currentContent = data.content || textToTiptapJSON(data.text || '');
    const editorContent = editor.current.getJSON();
    
    // 比较内容是否不同，避免不必要的更新
    const currentContentStr = JSON.stringify(currentContent);
    const editorContentStr = JSON.stringify(editorContent);
    
    if (currentContentStr !== editorContentStr) {
      try {
        isUpdatingFromDialogRef.current = true;
        editor.current.commands.setContent(currentContent as any);
      } catch {}
    }
  }, [data.content, data.text, isEditorFocused]);

  return (
    <NodeLayout
      id={id}
      data={data}
      title={title}
      type={type}
      variant="modern"
      collapsedAspectRatio="16/9"
      collapsedSummary={<TextCollapsedSummary data={data as any} />}
    >
      <TextTopToolbar
        nodeId={id}
        text={data.text}
        onOpenEditor={openEditDialog}
        onPaste={handlePaste}
        onSplit={handleSplit}
      />
      <NodeSurface
        bgStyle={bgStyle}
        radiusClass="rounded-3xl"
        insetClass="inset-1"
        aspectRatio="16/4.5"
        showDashed={false}
        onDoubleClick={enterEditMode}
      >
        <div 
          className="relative flex h-full items-center justify-center p-6"
          title={t('Double-click to input prompt...')}
        >
          {/* ReactFlow: prevent dragging the whole node when selecting text inside the editor */}
          <div
            className={cn(isEditing ? 'nodrag' : undefined, 'nowheel w-full cursor-text')}
            onKeyDownCapture={(e) => {
              // 仅在编辑模式下处理 Enter（插入 hard break），否则把快捷键留给画布
              if (!isEditing) return;

              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                exitEditMode();
                return;
              }
              handleHardBreakKeyDown(e);
            }}
          >
            <div className="relative flex items-center justify-center">
              {!isEditing && !((data.text || '').trim().length) && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <p className="text-base font-medium text-foreground">
                      {t('Ready to Create?')}
                    </p>
                    <p className="text-sm text-muted-foreground whitespace-nowrap">
                      {t('Double-click to input prompt...')}
                    </p>
                  </div>
                </div>
              )}
              <div ref={scrollContainerRef} className={cn('w-full', !isEditing ? 'max-h-[100px] overflow-y-auto custom-scrollbar' : 'max-h-[100px] overflow-y-auto custom-scrollbar')}>
                {editorMounted ? (
                  <EditorProvider
                    onCreate={handleCreate}
                    immediatelyRender={false}
                    content={data.content}
                    placeholder=""
                    editable={canEditInline && isEditing}
                    className={cn(
                      (isEditing ? 'nodrag ' : '') + 'w-full p-0 text-sm break-words',
                      // 未进入编辑时禁用指针事件：允许“点击内容区域=选中节点”，但不会把焦点送进编辑器
                      !isEditing ? 'pointer-events-none select-none' : undefined,
                      '[&>*:first-child]:mt-0',
                      '[&>*:last-child]:mb-0'
                    )}
                    onUpdate={handleUpdate}
                    onFocus={() => {
                      setIsEditorFocused(true);
                      setIsEditing(true);
                      try {
                        const ed = editor.current;
                        if (!ed) return;
                        requestAnimationFrame(() => {
                          try {
                            ed.commands.focus('end');
                            if (scrollContainerRef.current) {
                              scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                            }
                          } catch {}
                        });
                      } catch {}
                    }}
                    onBlur={() => {
                      setIsEditorFocused(false);
                      exitEditMode();
                    }}
                  />
                ) : (
                  // Lightweight read-only preview — replaces the heavy TipTap
                  // editor for prompt nodes that have not been selected yet.
                  // The editor mounts on first selection (sticky-mount) and
                  // stays mounted to avoid mount/unmount jank on re-selection.
                  <div className="w-full p-0 text-sm break-words whitespace-pre-wrap pointer-events-none select-none">
                    {data.text || ''}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {isLocked && !isOriginalOwner && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="px-3 py-1.5 rounded-full border text-xs text-muted-foreground bg-card/80">
              {t('Locked')}
            </div>
          </div>
        )}
      </NodeSurface>

      <TextEditDialogEditor
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        title={t('Edit Text')}
        initialContent={editContent}
        readOnly={isPreview}
        onSave={({ text, json }) => {
          updateNodeData(id, {
            content: json as any,
            text,
          });
          setIsEditDialogOpen(false);
          // 立即更新编辑器内容，避免等待 useEffect
          if (editor.current) {
            try {
              isUpdatingFromDialogRef.current = true;
              editor.current.commands.setContent(json as any);
            } catch {}
          }
        }}
        onCancel={() => setIsEditDialogOpen(false)}
        placeholder={t('Start typing...')}
      />
    </NodeLayout>
  );
};
